import { DatePicker, DateTimePicker } from '@offline-mui/x-date-pickers'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import type { ReactNode } from 'react'

/**
 * Date inputs used everywhere in the app. They always render as
 * MM/DD/YYYY (and MM/DD/YYYY HH:mm for date-times) regardless of the
 * browser locale, while the values they emit stay the ISO-like wall-clock
 * strings the rest of the app and the API already use:
 *   DateField      → "YYYY-MM-DD"        or ""
 *   DateTimeField  → "YYYY-MM-DDTHH:mm"  or ""
 */

export const DATE_FORMAT = 'MM/DD/YYYY'
export const DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm'

type CommonProps = {
  label: string
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
  error?: boolean
  helperText?: ReactNode
  size?: 'small' | 'medium'
  fullWidth?: boolean
  /** Earliest selectable day, "YYYY-MM-DD". */
  minDate?: string
}

function parse(value: string): Dayjs | null {
  if (!value) return null
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed : null
}

function textFieldProps({
  required,
  error,
  helperText,
  size,
  fullWidth = true,
}: Pick<CommonProps, 'required' | 'error' | 'helperText' | 'size' | 'fullWidth'>) {
  return { required, error, helperText, size, fullWidth }
}

export function DateField({
  label,
  value,
  onChange,
  minDate,
  disabled,
  ...rest
}: CommonProps) {
  return (
    <DatePicker
      label={label}
      format={DATE_FORMAT}
      value={parse(value)}
      onChange={(next) => onChange(next && next.isValid() ? next.format('YYYY-MM-DD') : '')}
      minDate={minDate ? dayjs(minDate) : undefined}
      disabled={disabled}
      slotProps={{ textField: textFieldProps(rest) }}
    />
  )
}

export function DateTimeField({
  label,
  value,
  onChange,
  minDate,
  disabled,
  ...rest
}: CommonProps) {
  return (
    <DateTimePicker
      label={label}
      format={DATE_TIME_FORMAT}
      ampm={false}
      value={parse(value)}
      onChange={(next) =>
        onChange(next && next.isValid() ? next.format('YYYY-MM-DDTHH:mm') : '')
      }
      minDateTime={minDate ? dayjs(minDate) : undefined}
      disabled={disabled}
      slotProps={{ textField: textFieldProps(rest) }}
    />
  )
}
